/**
 * Thin API client. Cookies carry the session; mutating requests echo the CSRF cookie in a header
 * (double-submit). Errors are normalised to ApiError with the server's stable error code.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: any,
    public requestId?: string,
  ) {
    super(message);
  }
  /** Field → message map for forms (from 422 validation details). */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details) if (d.field) out[d.field] = d.message;
    } else if (this.details?.field) {
      out[this.details.field] = this.message;
    }
    return out;
  }
}

function csrf() {
  const m = document.cookie.match(/(?:^|; )hc_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

export const newIdempotencyKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

type Query = Record<string, string | number | boolean | undefined | null | string[]>;

export function qs(params?: Query) {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) continue;
    sp.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function request<T>(method: string, path: string, body?: unknown, opts: { idempotencyKey?: string; query?: Query } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  if (method !== 'GET') headers['x-csrf-token'] = csrf();
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}${qs(opts.query)}`, { method, headers, body: payload, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Check your connection and try again.');
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'ERROR', e.message ?? `Request failed (${res.status})`, e.details, e.requestId);
  }
  return data as T;
}

export const api = {
  get: <T = any>(path: string, query?: Query) => request<T>('GET', path, undefined, { query }),
  post: <T = any>(path: string, body?: unknown, idempotencyKey?: string) => request<T>('POST', path, body ?? {}, { idempotencyKey }),
  put: <T = any>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T = any>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T = any>(path: string) => request<T>('DELETE', path),
  upload: <T = any>(path: string, file: File, query?: Query) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<T>('POST', `${path}${qs(query)}`, fd);
  },
};

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}
